import { createAppReducer, apiReducerSwitch } from 'lib/redux/reducerUtils'
import {
  API_CALL_REQUEST,
  API_CALL_FAILURE,
  API_CALL_SUCCESS,
  CLEAR_BOOKING_CALENDAR,
  CALENDAR_UPDATE_PRICING_DATA,
} from 'actions/actionConstants'
import { FETCH_DAY_CALENDAR, FETCH_CALENDARS, FETCH_CALENDARS_BY_OCCUPANCY, FETCH_TOUR_DATES, FETCH_AVAILABILITY_DATE_RANGE } from 'actions/apiActionConstants'
import { getCalendarDateCoordinates } from 'lib/datetime/calendarUtils'
import { omitKeys } from 'lib/object/objectUtils'

const initialState: App.CalendarState = {
  calendarRequestScopes: {},
  calendarsByPackageKey: {},
  calendarsByPackageKeyLereGroups: {},
  calendarsByOccupancy: {},
  calendarsByOccupancyLereGroups: {},
  calendarsByOccupancyLoading: {},
  calendarsLoading: {},
  calendarErrors: {},
  tourDates: {},
  availabilityDateRange: {},
}

const apiCalls = apiReducerSwitch<App.CalendarState>({
  [FETCH_DAY_CALENDAR]: {
    request: (state, action) => ({
      calendarsLoading: {
        ...state.calendarsLoading,
        [action.key]: true,
      },
      calendarErrors: {
        ...state.calendarErrors,
        [action.key]: undefined,
      },
    }),
    success: (state, action) => ({
      calendarsByPackageKey: {
        ...state.calendarsByPackageKey,
        [action.key]: {
          ...state.calendarsByPackageKey[action.key],
          ...action.data,
        },
      },
      calendarsLoading: {
        ...state.calendarsLoading,
        [action.key]: false,
      },
    }),
    failure: (state, action) => {
      const calendarsByPackageKey = { ...state.calendarsByPackageKey }
      delete calendarsByPackageKey[action.key]

      return {
        calendarsByPackageKey,
        calendarsLoading: {
          ...state.calendarsLoading,
          [action.key]: false,
        },
        calendarErrors: {
          ...state.calendarErrors,
          [action.key]: action.error,
        },
      }
    },
  },
  [FETCH_CALENDARS]: {
    request: (state, action) => {
      const responsePackageKeys = action.responsePackageKeys ?? action.packageKeys
      const nextLoading: Record<string, boolean> = {
        ...state.calendarsLoading,
        [action.offerId]: true,
      }
      responsePackageKeys.forEach((key: string) => {
        nextLoading[key] = true
      })
      const nextErrors = {
        ...state.calendarErrors,
      }
      delete nextErrors[action.offerId]
      const nextCalendars = { ...state.calendarsByPackageKey }
      const nextLereGroups = { ...state.calendarsByPackageKeyLereGroups }
      if (action.requestScope !== undefined) {
        responsePackageKeys.forEach(key => {
          if (state.calendarRequestScopes?.[key] !== action.requestScope) {
            delete nextCalendars[key]
            delete nextLereGroups[key]
          }
        })
      }

      return {
        calendarRequestScopes: {
          ...state.calendarRequestScopes,
          ...Object.fromEntries(responsePackageKeys.map(key => [key, action.requestScope])),
        },
        calendarsLoading: nextLoading,
        calendarErrors: nextErrors,
        ...(action.requestScope !== undefined && { calendarsByPackageKey: nextCalendars, calendarsByPackageKeyLereGroups: nextLereGroups }),
      }
    },
    success: (state, action) => {
      const requestScopes = state.calendarRequestScopes
      const packageKeys = (action.responsePackageKeys ?? action.packageKeys).filter(key => requestScopes && Object.hasOwn(requestScopes, key) && requestScopes[key] === action.requestScope)
      if (!packageKeys.length) return
      const nextLoading: App.CalendarState['calendarsLoading'] = {
        ...state.calendarsLoading,
        [action.offerId]: false,
      }
      packageKeys.forEach((key: string) => { nextLoading[key] = false })
      const nextCalendars = { ...state.calendarsByPackageKey }
      const ownedPackageKeys = new Set(packageKeys)
      action.data.forEach((data: App.Calendar) => {
        if (ownedPackageKeys.has(data.uniqueKey)) nextCalendars[data.uniqueKey] = data
      })
      const nextLereGroups = {
        ...state.calendarsByPackageKeyLereGroups,
        ...Object.fromEntries(packageKeys.map((key: string) => [key, action.lereGroup])),
      }

      // Packages that were requested but absent from the API response (e.g. region-filtered packages
      // fetched without bypassRegionFilter) must be stored as empty calendars. Without this,
      // calendarsByPackageKey[key] stays undefined and the dedup check in fetchCalendars keeps
      // re-dispatching on every render, creating an infinite request loop.
      action.packageKeys.filter(key => ownedPackageKeys.has(key)).forEach((key: string) => {
        if (!nextCalendars[key]) {
          nextCalendars[key] = { uniqueKey: key, months: [], flightCacheActive: false, flightsNotAvailable: false }
        }
      })

      return {
        calendarsLoading: nextLoading,
        calendarsByPackageKey: nextCalendars,
        calendarsByPackageKeyLereGroups: nextLereGroups,
      }
    },
    failure: (state, action) => {
      const requestScopes = state.calendarRequestScopes
      const packageKeys = (action.responsePackageKeys ?? action.packageKeys).filter(key => requestScopes && Object.hasOwn(requestScopes, key) && requestScopes[key] === action.requestScope)
      if (!packageKeys.length) return
      const nextLoading: App.CalendarState['calendarsLoading'] = {
        ...state.calendarsLoading,
        [action.offerId]: false,
      }
      const nextCalendars = { ...state.calendarsByPackageKey }
      const nextLereGroups = { ...state.calendarsByPackageKeyLereGroups }
      const nextErrors: App.CalendarState['calendarErrors'] = {
        ...state.calendarErrors,
        [action.offerId]: action.error,
      }

      packageKeys.forEach((key: string) => {
        delete nextCalendars[key]
        delete nextLereGroups[key]
        nextErrors[key] = action.error
        nextLoading[key] = false
      })

      return {
        calendarsLoading: nextLoading,
        calendarsByPackageKey: nextCalendars,
        calendarsByPackageKeyLereGroups: nextLereGroups,
        calendarErrors: nextErrors,
      }
    },
  },
  [FETCH_CALENDARS_BY_OCCUPANCY]: {
    request: (state, action) => {
      const pkgKeys = action.pkgKeys
      const loadingState = Object.fromEntries(pkgKeys.map(key => [key, true]))
      return {
        calendarsByOccupancyLoading: {
          ...state.calendarsByOccupancyLoading,
          [action.occupancyKey]: {
            ...state.calendarsByOccupancyLoading[action.occupancyKey],
            ...loadingState,
          },
        },
        calendarErrors: omitKeys(Object.keys(loadingState), state.calendarErrors),
      }
    },
    success: (state, action) => {
      const pkgKeys = action.pkgKeys
      const occupancyKey = action.occupancyKey

      const newLoadingState = {
        ...state.calendarsByOccupancyLoading,
        [occupancyKey]: {
          ...state.calendarsByOccupancyLoading[occupancyKey],
          ...Object.fromEntries(pkgKeys.map(key => [key, false])),
        },
      }

      const newCalendarState: App.CalendarState['calendarsByOccupancy'] = {
        ...state.calendarsByOccupancy,
        [occupancyKey]: {
          ...state.calendarsByOccupancy[occupancyKey],
          ...action.data,
        },
      }
      const newLereGroupState: App.CalendarState['calendarsByOccupancyLereGroups'] = {
        ...state.calendarsByOccupancyLereGroups,
        [occupancyKey]: {
          ...state.calendarsByOccupancyLereGroups?.[occupancyKey],
          ...Object.fromEntries(pkgKeys.map(key => [key, action.lereGroup])),
        },
      }

      return {
        calendarsByOccupancyLoading: newLoadingState,
        calendarsByOccupancy: newCalendarState,
        calendarsByOccupancyLereGroups: newLereGroupState,
      }
    },
    failure: (state, action) => {
      const occupancyKey = action.occupancyKey
      const pkgKeys = action.pkgKeys
      const loadingState = Object.fromEntries(pkgKeys.map(key => [key, false]))

      const newLoadingState = {
        ...state.calendarsByOccupancyLoading,
        [occupancyKey]: {
          ...state.calendarsByOccupancyLoading[occupancyKey],
          ...loadingState,
        },
      }

      const occupancyCalendarState = omitKeys(pkgKeys, state.calendarsByOccupancy[occupancyKey])
      const occupancyLereGroupState = omitKeys(pkgKeys, state.calendarsByOccupancyLereGroups?.[occupancyKey])
      const newCalendarState = {
        ...state.calendarsByOccupancy,
        [occupancyKey]: occupancyCalendarState,
      }
      const newLereGroupState = {
        ...state.calendarsByOccupancyLereGroups,
        [occupancyKey]: occupancyLereGroupState,
      }

      const newErrorState = {
        ...state.calendarErrors,
        ...Object.fromEntries(pkgKeys.map(key => [key, action.error])),
      }

      return {
        calendarsByOccupancyLoading: newLoadingState,
        calendarsByOccupancy: newCalendarState,
        calendarsByOccupancyLereGroups: newLereGroupState,
        calendarErrors: newErrorState,
      }
    },
  },
  [FETCH_TOUR_DATES]: {
    request: (state, action) => {
      let nextErrors = state.calendarErrors
      if (state.calendarErrors[action.key]) {
        nextErrors = {
          ...state.calendarErrors,
          [action.key]: undefined,
        }
      }

      return {
        calendarsLoading: {
          ...state.calendarsLoading,
          [action.key]: true,
        },
        calendarErrors: nextErrors,
      }
    },
    success: (state, action) => ({
      calendarsLoading: {
        ...state.calendarsLoading,
        [action.key]: false,
      },
      tourDates: {
        ...state.tourDates,
        [action.key]: action.data,
      },
    }),
    failure: (state, action) => ({
      calendarsLoading: {
        ...state.calendarsLoading,
        [action.key]: false,
      },
      calendarErrors: {
        ...state.calendarErrors,
        [action.key]: action.error,
      },
    }),
  },
  [FETCH_AVAILABILITY_DATE_RANGE]: {
    request: (state, action) => ({
      availabilityDateRange: {
        ...state.availabilityDateRange,
        [action.offerId]: {
          data: state.availabilityDateRange[action.offerId]?.data ?? {},
          fetching: true,
          error: undefined,
        },
      },
    }),
    success: (state, action) => {
      // Aborted requests can resolve without payload; preserve existing cached data.
      const nextData = action.data ?? state.availabilityDateRange[action.offerId]?.data ?? {}

      return {
        availabilityDateRange: {
          ...state.availabilityDateRange,
          [action.offerId]: {
            data: nextData,
            fetching: false,
            error: undefined,
          },
        },
      }
    },
    failure: (state, action) => ({
      availabilityDateRange: {
        ...state.availabilityDateRange,
        [action.offerId]: {
          data: state.availabilityDateRange[action.offerId]?.data ?? {},
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
})

const calendarReducer = createAppReducer<App.CalendarState>(initialState, {
  [API_CALL_REQUEST]: (state, action) => apiCalls(state, action),
  [API_CALL_FAILURE]: (state, action) => apiCalls(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiCalls(state, action),
  [CLEAR_BOOKING_CALENDAR]: () => initialState,
  [CALENDAR_UPDATE_PRICING_DATA]: (state, action) => {
    const calendar = action.occupancy ?
      state.calendarsByOccupancy[action.occupancy][action.uniqueKey] :
      state.calendarsByPackageKey[action.uniqueKey]

    const { monthIdx, dayIdx } = getCalendarDateCoordinates(action.checkIn, calendar)

    const month = calendar.months[monthIdx]
    const day = month.days[dayIdx]

    const newDay = {
      ...day,
      ...action.data,
    }

    const newDayList = [...month.days.slice(0, dayIdx), newDay, ...month.days.slice(dayIdx + 1)]
    const newMonth = {
      ...month,
      days: newDayList,
    }

    const newMonthList = [...calendar.months.slice(0, monthIdx), newMonth, ...calendar.months.slice(monthIdx + 1)]
    const newCalendar = {
      ...calendar,
      months: newMonthList,
    }

    const newCalendarsByOccupancy: App.CalendarState['calendarsByOccupancy'][string] = {
      ...state.calendarsByOccupancy[action.occupancy],
      [action.uniqueKey]: newCalendar,
    }

    return {
      ...state,
      calendarsByOccupancy: {
        [action.occupancy]: newCalendarsByOccupancy,
      },
    }
  },
})

export default calendarReducer
